import React, { useState, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search, ArrowBack, Download, FilterList } from "@mui/icons-material";

const AllTeachersAnalytics = () => {
  const navigate = useNavigate();
  const { isInInstituteContext, selectedInstitute } = useSelector((state) => state.instituteContext);
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.role?.slug || user?.role || "teacher";
  const userInstitute = user?.institute?.name || "ABC Engineering College";
  const canViewGlobalData = userRole === "superadmin" && !isInInstituteContext;

  const [searchQuery, setSearchQuery] = useState("");
  const [instituteFilter, setInstituteFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [institutes, setInstitutes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsLoading(true);
      try {
        const analyticsService = (await import("../../services/analyticsService")).default;
        const instituteService = (await import("../../services/instituteService")).default;
        const deptService = (await import("../../services/departmentService")).default;

        if (canViewGlobalData) {
          const instRes = await instituteService.getInstitutes({ page: 1, limit: 50 });
          if (mounted && instRes?.data?.data) setInstitutes(instRes.data.data);
        }

        const deptRes = await deptService.getAllDepartments({ page: 1, limit: 100 });
        if (mounted && deptRes?.data?.data) setDepartments(deptRes.data.data);

        const teachersRes = await analyticsService.getTeachers({
          page: 1,
          limit: 1000,
          ...(instituteFilter && canViewGlobalData ? { instituteId: instituteFilter } : {}),
          ...(departmentFilter ? { departmentId: departmentFilter } : {}),
        });
        if (mounted) {
          const payload = teachersRes?.data?.data || teachersRes?.data || [];
          setTeachers(Array.isArray(payload) ? payload : []);
        }
      } catch (e) {
        console.warn("Failed to load teachers analytics:", e.message || e);
        if (mounted) setTeachers([]);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    load();
    return () => (mounted = false);
  }, [canViewGlobalData, instituteFilter, departmentFilter]);

  const columns = [
    {
      field: "name",
      headerName: "Teacher",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "secondary.main", width: 36, height: 36 }}>
            {(params.row.name || "?").split(" ").pop().charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.empId}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: "email", headerName: "Email", width: 180 },
    { field: "institute", headerName: "Institute", width: 200 },
    { field: "department", headerName: "Department", width: 150 },
    { field: "designation", headerName: "Designation", width: 150 },
    { field: "subjects", headerName: "Subjects", width: 100 },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "active" ? "success" : "default"}
        />
      ),
    },
  ];

  const filteredTeachers = teachers.filter((teacher) => {
    const matchesSearch =
      (teacher.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (teacher.email || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (teacher.empId || teacher.teacherLoginID || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || (teacher.status || "active") === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Get page title based on context
  const getPageTitle = () => {
    if (canViewGlobalData) return "All Teachers";
    if (isInInstituteContext) return `Teachers - ${selectedInstitute?.name}`;
    return `Teachers - ${userInstitute}`;
  };

  const getPageSubtitle = () => {
    if (canViewGlobalData)
      return "View and filter all teachers across institutes";
    if (isInInstituteContext)
      return `Viewing teachers of ${selectedInstitute?.name}`;
    return `Viewing teachers of your institute`;
  };

  const stats = [
    {
      label: "Total Teachers",
      value: filteredTeachers.length,
      color: "secondary.main",
    },
    {
      label: "Active",
      value: filteredTeachers.filter((t) => t.status === "active").length,
      color: "success.main",
    },
    {
      label: "HODs",
      value: filteredTeachers.filter((t) => t.designation === "HOD").length,
      color: "primary.main",
    },
    {
      label: "Professors",
      value: filteredTeachers.filter((t) => t.designation === "Professor")
        .length,
      color: "warning.main",
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {getPageTitle()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getPageSubtitle()}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Download />}>
          Export
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <FilterList color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search teachers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {canViewGlobalData && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Institute</InputLabel>
                  <Select
                    value={instituteFilter}
                    label="Institute"
                    onChange={(e) => setInstituteFilter(e.target.value)}
                  >
                    <MenuItem value="">All Institutes</MenuItem>
                    {institutes.map((inst) => (
                      <MenuItem key={inst.id} value={String(inst.id)}>
                        {inst.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Department"
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={String(dept.id)}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card>
        <CardContent>
          <DataGrid
            rows={filteredTeachers}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{ border: "none" }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default AllTeachersAnalytics;
